import {useState, useMemo, useEffect} from "react";
import {useForm} from "antd/lib/form/Form";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {set} from "mobx";

export const useShiftRequest = () => {
    const stores = useStore();
    const [selectedCompany, setSelectedCompany] = useState();
    const [selectedSubCompany, setSelectedSubCompany] = useState();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState(
        moment()
            .startOf("month")
            .format("YYYY-MM-DD")
    );
    const [selectedEndDate, setSelectedEndDate] = useState(
        moment()
            .endOf("month")
            .format("YYYY-MM-DD")
    );
    const [form] = useForm();
    const [shiftRequests, setShiftRequests] = useState([]);
    const [selectedShiftDay, setSelectedShiftDay] = useState(null);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [selectedDeletingShiftId, setSelectedDeletingShiftId] = useState(null);
    const [selectedShiftRequest, setSelectedShiftRequest] = useState(null);
    const [isShiftRequestUpdating, setIsShiftRequestUpdating] = useState(false);

    useEffect(() => {
        if (selectedCompany && selectedSubCompany) {
            getShiftRequests({
                startDate: selectedStartDate,
                endDate: selectedEndDate
            });
        }
    }, [selectedCompany, selectedSubCompany]);

    const [isShiftModalOpen, setShiftIsModalOpen] = useState(false);

    const handleOnCreateShiftRequest = async values => {
        const data = {
            companyId: selectedCompany,
            subCompanyId: selectedSubCompany,
            repeatStartDate: values?.shiftDate?.[0].format("YYYY-MM-DD"),
            repeatEndDate: values?.shiftDate?.[1].format("YYYY-MM-DD"),
            requestStartTime: values?.shiftTime?.[0].format("HH:mm"),
            requestEndTime: values?.shiftTime?.[1].format("HH:mm"),
            personCount: Number(values?.courierCount),
            shiftNote: values?.shiftNote
        };

        const response = await stores.shiftRequestStore.createShiftRequest(data);

        if (response) {
            getShiftRequests({
                startDate: selectedStartDate,
                endDate: selectedEndDate
            });
            setIsCreateModalOpen(false);
        }
    };

    const fetchShiftRequests = async params => {
        const response = await stores.shiftRequestStore.getShiftRequests(params);
        return response?.rows || [];
    };

    const getShiftRequests = async ({startDate, endDate, status = "all", city = "all"}) => {
        const shiftRequestsData = await fetchShiftRequests({
            companyId: selectedCompany,
            subcompany: selectedSubCompany,
            startDate: startDate ?? selectedStartDate,
            endDate: endDate ?? selectedEndDate,
            status,
            city
        });

        const data = shiftRequestsData.reduce((acc, shiftRequest) => {
            const date = moment(shiftRequest.date).format("YYYY-MM-DD");
            if (!acc[date]) {
                acc[date] = [];
            }

            acc[date].push(shiftRequest);

            return acc;
        }, {});

        setShiftRequests(data);
    };

    const onPanelChange = newValue => {
        const newStartDate = moment(newValue)
            .startOf("month")
            .format("YYYY-MM-DD");
        const newEndDate = moment(newValue)
            .endOf("month")
            .format("YYYY-MM-DD");

        getShiftRequests({
            startDate: newStartDate,
            endDate: newEndDate
        });

        setSelectedStartDate(newStartDate);
        setSelectedEndDate(newEndDate);
    };

    const handleOnDaySelect = selectedDay => {
        setShiftIsModalOpen(true);
        setSelectedShiftDay(selectedDay.format("YYYY-MM-DD"));
    };

    const handleDeleteShiftRequest = async id => {
        const response = await stores.shiftRequestStore.deleteShiftRequest({
            _id: id
        });

        if (response) {
            setTimeout(() => {
                setIsDeleteConfirmModalOpen(false);
            }, 500);
            setTimeout(() => {
                setShiftIsModalOpen(false);
                getShiftRequests({
                    startDate: selectedStartDate,
                    endDate: selectedEndDate
                });
            }, 1000);
        }
    };

    const handleOnUpdateShiftRequest = async values => {
        const data = {
            id: selectedShiftRequest._id,
            requestDate: values?.shiftDate?.format("YYYY-MM-DD"),
            requestStartTime: values?.shiftTime?.[0].format("HH:mm"),
            requestEndTime: values?.shiftTime?.[1].format("HH:mm"),
            personCount: Number(values?.courierCount),
            shiftNote: values?.shiftNote
        };

        const response = await stores.shiftRequestStore.updateShiftRequest(data);

        if (response) {
            getShiftRequests({
                startDate: selectedStartDate,
                endDate: selectedEndDate
            });
            setIsCreateModalOpen(false);
        }
    };

    useEffect(() => {
        if (!isShiftModalOpen) {
            setSelectedShiftDay(null);
        }
    }, [isShiftModalOpen]);

    return {
        selectedCompany,
        setSelectedCompany,
        selectedSubCompany,
        setSelectedSubCompany,
        isCreateModalOpen,
        setIsCreateModalOpen,
        shiftRequestForm: form,
        handleOnCreateShiftRequest,
        shiftRequests,
        setSelectedStartDate,
        setSelectedEndDate,
        selectedStartDate,
        selectedEndDate,
        selectedShiftDay,
        setSelectedShiftDay,
        onPanelChange,
        handleOnDaySelect,
        setShiftIsModalOpen,
        isShiftModalOpen,
        handleDeleteShiftRequest,
        isDeleteConfirmModalOpen,
        setIsDeleteConfirmModalOpen,
        selectedDeletingShiftId,
        setSelectedDeletingShiftId,
        selectedShiftRequest,
        setSelectedShiftRequest,
        isShiftRequestUpdating,
        setIsShiftRequestUpdating,
        handleOnUpdateShiftRequest
    };
};
